import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../utils/authAxios';

/**
 * AI Description Generator Button
 * @param {string} entityType - 'center', 'professional', or 'workshop'
 * @param {string} name - Name of the entity
 * @param {object} context - Additional context (specialization, city, etc.)
 * @param {function} onGenerated - Callback with generated description
 */
function AIDescriptionButton({ entityType, name, context, onGenerated, disabled }) {
  const [loading, setLoading] = useState(false);

  async function generateDescription() {
    if (!name || name.trim() === '') {
      toast.error('Por favor ingresa un nombre primero');
      return;
    }

    setLoading(true);
    try {
      const res = await authAxios.post(API + '/ai/generate-description', {
        entity_type: entityType,
        name: name,
        context: context || {}
      }, { withCredentials: true });

      if (res.data.success && res.data.description) {
        onGenerated(res.data.description);
        toast.success('Descripción generada con IA');
      } else {
        toast.error('No se pudo generar la descripción');
      }
    } catch (e) {
      console.error('AI generation error:', e);
      toast.error(e.response?.data?.detail || 'Error al generar descripción');
    }
    setLoading(false);
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={generateDescription}
      disabled={loading || disabled}
      className="gap-2 text-purple-600 border-purple-200 hover:bg-purple-50 hover:text-purple-700"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Wand2 className="w-4 h-4" />
      )}
      {loading ? 'Generando...' : 'Generar con IA'}
    </Button>
  );
}

/**
 * AI Description Field - Complete field with label, textarea and AI button
 */
function AIDescriptionField({ 
  label, 
  value, 
  onChange, 
  entityType, 
  entityName, 
  context,
  placeholder,
  rows = 4
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <AIDescriptionButton
          entityType={entityType}
          name={entityName}
          context={context}
          onGenerated={onChange}
        />
      </div>
      <textarea
        value={value}
        onChange={function(e) { onChange(e.target.value); }}
        placeholder={placeholder || 'Escribe una descripción o genera una con IA...'}
        rows={rows}
        className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      />
    </div>
  );
}

export { AIDescriptionButton, AIDescriptionField };
export default AIDescriptionButton;
